// frontend/src/components/Profile/EditProfileModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { authAPI } from '../../services/api';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ (как в EditProjectModal)
// ============================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    maxWidth: 600,
    minWidth: 500,
    maxHeight: '90vh',
    overflow: 'hidden',
    zIndex: 1300,
    backgroundColor: theme.palette.grey[50],
    boxShadow: theme.shadows[0]
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.grey[100],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.grey[600],
    },
  }
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const EditProfileModal = ({ open, onClose, user, onSave }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Состояния формы
  const [formData, setFormData] = useState({
    last_name: '',
    first_name: '',
    middle_name: '',
    bio: ''
  });

  // Загружаем данные пользователя при открытии
  useEffect(() => {
    if (open && user) {
      setFormData({
        last_name: user.last_name || '',
        first_name: user.first_name || '',
        middle_name: user.middle_name || '',
        bio: user.bio || ''
      });
      setError('');
      setSuccess(false);
    }
  }, [open, user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.last_name.trim()) {
      setError('Введите фамилию');
      return false;
    }
    if (!formData.first_name.trim()) {
      setError('Введите имя');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Отправляем только те поля, которые изменились
      const submitData = {
        last_name: formData.last_name,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        bio: formData.bio
      };

      console.log('📦 Отправка данных на сервер:', submitData);

      const response = await authAPI.updateProfile(submitData);

      console.log('✅ Профиль обновлен:', response);

      setSuccess(true);

      setTimeout(() => {
        if (onSave) onSave(response);
        onClose();
      }, 1500);

    } catch (err) {
      console.error('❌ Ошибка обновления профиля:', err);
      setError(err.message || 'Не удалось обновить профиль');
    } finally {
      setLoading(false);
    }
  };

  const getFullNamePreview = () => {
    const parts = [];
    if (formData.last_name) parts.push(formData.last_name);
    if (formData.first_name) parts.push(formData.first_name);
    if (formData.middle_name) parts.push(formData.middle_name);
    return parts.join(' ') || 'Не указано';
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth={false}>
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="700">
            Редактирование профиля
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', borderRadius: theme.spacing(1.5) }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Превью текущего имени */}
        <Box sx={{
          mb: 3,
          p: 2,
          bgcolor: 'white',
          borderRadius: 3,
          border: '0.5px solid',
          borderColor: theme.palette.divider
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Предпросмотр
          </Typography>
          <Typography variant="h6" fontWeight="600" color="primary.main">
            {getFullNamePreview()}
          </Typography>
          {formData.bio && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formData.bio.length > 100 ? formData.bio.slice(0, 100) + '...' : formData.bio}
            </Typography>
          )}
        </Box>

        {success && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: theme.spacing(2) }}>
            Профиль успешно обновлен!
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: theme.spacing(2) }}>
            {error}
          </Alert>
        )}

        {/* Раздел: Личная информация */}
        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="primary" />
          Личная информация
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
          <TextField
            fullWidth
            label="Фамилия"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            placeholder="Введите фамилию"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />

          <TextField
            fullWidth
            label="Имя"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            placeholder="Введите имя"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />

          <TextField
            fullWidth
            label="Отчество"
            name="middle_name"
            value={formData.middle_name}
            onChange={handleInputChange}
            placeholder="Введите отчество"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Раздел: О себе */}
        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" color="primary" />
          О себе
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2 }}>
          <TextField
            fullWidth
            label="О себе"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="Расскажите о себе, своих научных интересах и достижениях..."
            multiline
            rows={4}
            helperText="Максимум 1000 символов"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
          />
        </Box>
      </StyledDialogContent>

      <DialogActions sx={{ p: 3, pt: 0, gap: 1, justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: 5, px: 4 }}
        >
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ borderRadius: 5, px: 4 }}
        >
          {loading ? 'Сохранение...' : 'Сохранить изменения'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditProfileModal;